import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentApi } from "../../../modules/agent/api";
import { AgentMentoringFilterPageRequest, CreateUpdateAgentMentoring } from "../../../modules/agent/types";
import type { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";

export const AGENT_MENTORINGS_QUERY_KEYS = {
  filterAgentMentorings: (filter?: AgentMentoringFilterPageRequest) =>
    filter ? ["getAgentMentorings", filter] : ["getAgentMentorings"],
  createAgentMentoring: () => ["createAgentMentoring"],
  updateAgentMentoring: () => ["updateAgentMentoring"],
  deleteAgentMentoring: () => ["deleteAgentMentoring"],
};

export const useFilterAgentMentorings = (payload: AgentMentoringFilterPageRequest) => {
  return useQuery({
    queryKey: AGENT_MENTORINGS_QUERY_KEYS.filterAgentMentorings(payload),
    queryFn: async () => {
      const { data } = await agentApi.filterAgentMentorings(payload);

      return data;
    },
  });
};

export const useCreateAgentMentoring = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_MENTORINGS_QUERY_KEYS.createAgentMentoring(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateAgentMentoring) => {
      const { data } = await agentApi.createAgentMentoring(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_MENTORINGS_QUERY_KEYS.filterAgentMentorings() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentMentoring = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_MENTORINGS_QUERY_KEYS.updateAgentMentoring(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAgentMentoring>) => {
      const { data } = await agentApi.updateAgentMentoring(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_MENTORINGS_QUERY_KEYS.filterAgentMentorings() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentMentoring = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_MENTORINGS_QUERY_KEYS.deleteAgentMentoring(),
    mutationFn: (id: UUID) => {
      return agentApi.deleteAgentMentoring({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_MENTORINGS_QUERY_KEYS.filterAgentMentorings() });
      Notification.itemDeletedNotification();
    },
  });
};
